/**
 * Attachment Card Component
 * 
 * Displays a single uploaded file with preview, metadata, and actions.
 * Per design.md: lucide icons, semantic tokens, subtle hover states.
 */

import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
    Card,
    Button,
    Badge,
    Loader2,
    Trash2,
    Download,
    FileText,
    Image as ImageIcon,
    File,
    FileSpreadsheet,
    Archive,
} from '@uprosal/ui';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface AttachmentCardProps {
    attachment: {
        _id: Id<'profileAttachments'>;
        filename: string;
        mimeType: string;
        size: number;
        label?: string;
        url: string | null;
        createdAt: number;
    };
    onDelete?: () => void;
}

// Format file size to human-readable format
function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Get icon based on MIME type
function getFileIcon(mimeType: string) {
    if (mimeType.startsWith('image/')) {
        return <ImageIcon className="w-5 h-5 text-primary" />;
    }
    if (mimeType === 'application/pdf') {
        return <FileText className="w-5 h-5 text-destructive" />;
    }
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
        return <FileSpreadsheet className="w-5 h-5 text-success" />;
    }
    if (mimeType.includes('zip') || mimeType.includes('archive')) {
        return <Archive className="w-5 h-5 text-warning" />;
    }
    return <File className="w-5 h-5 text-muted-foreground" />;
}

// Get file type label
function getFileTypeLabel(mimeType: string): string {
    if (mimeType.startsWith('image/')) {
        const format = mimeType.split('/')[1]?.toUpperCase();
        return format || 'Image';
    }
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType.includes('word')) return 'Word';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'PPT';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Excel';
    if (mimeType.includes('zip')) return 'ZIP';
    return 'File';
}

export function AttachmentCard({ attachment, onDelete }: AttachmentCardProps) {
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const deleteAttachment = useMutation(api.mutations.attachments.deleteAttachment);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteAttachment({ attachmentId: attachment._id });
            onDelete?.();
        } catch (error) {
            console.error('Failed to delete attachment:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleDownload = () => {
        if (attachment.url) {
            window.open(attachment.url, '_blank');
        }
    };

    const isImage = attachment.mimeType.startsWith('image/');

    return (
        <>
            <Card className="bg-card border-border hover:border-primary/30 transition-colors group overflow-hidden">
                {/* Image Preview */}
                {isImage && attachment.url && (
                    <div className="aspect-video bg-muted overflow-hidden">
                        <img
                            src={attachment.url}
                            alt={attachment.filename}
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}

                {/* Non-image file icon */}
                {!isImage && (
                    <div className="aspect-video bg-muted/50 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                            {getFileIcon(attachment.mimeType)}
                        </div>
                    </div>
                )}

                {/* File info */}
                <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <p
                                className="text-sm font-medium text-foreground truncate"
                                title={attachment.label || attachment.filename}
                            >
                                {attachment.label || attachment.filename}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                    {getFileTypeLabel(attachment.mimeType)}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                    {formatFileSize(attachment.size)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={handleDownload}
                            disabled={!attachment.url}
                        >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            Download
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-destructive hover:text-destructive"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <>
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                    Delete
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={showDeleteConfirm}
                onOpenChange={setShowDeleteConfirm}
                title="Delete Attachment"
                description={`Are you sure you want to delete "${attachment.label || attachment.filename}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                confirmLabel="Delete"
                variant="destructive"
            />
        </>
    );
}
